import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Slack } from '@air/api';
import { type Board, type ListSlackIntegrationsObject, type ListSlackIntegrationsResponse } from '@air/api/types';
import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { getBoardSlackIntegrationsKey } from './useBoardSlackIntegrations';

export type UpdateBoardSlackIntegrationProps = {
  integrationId: ListSlackIntegrationsObject['integrationId'];
  receiverId: ListSlackIntegrationsObject['receiverId'];
  channelName: string;
  boardId?: Board['id'];
  includeDescendants: boolean;
};

export const useUpdateBoardSlackIntegration = () => {
  const { currentWorkspace } = useCurrentWorkspace();
  const queryClient = useQueryClient();
  const { showToast } = useToasts();

  const mutation = useMutation<
    void,
    unknown,
    UpdateBoardSlackIntegrationProps,
    { prevIntegrations: ListSlackIntegrationsResponse | undefined }
  >({
    mutationFn: async ({ receiverId, boardId, includeDescendants }) => {
      const workspaceId = currentWorkspace?.id;
      if (!workspaceId) {
        throw new Error('No workspace id');
      }

      if (boardId) {
        await Slack.updateIntegrationForBoard({
          workspaceId,
          integration: { receiverId },
          includeDescendants,
          boardId,
        });
        await queryClient.invalidateQueries({
          queryKey: getBoardSlackIntegrationsKey(boardId),
        });
      }
    },

    onMutate: async (props) => {
      const key = getBoardSlackIntegrationsKey(props.boardId!);
      const prevIntegrations = queryClient.getQueryData<ListSlackIntegrationsResponse | undefined>(key);

      queryClient.setQueryData<ListSlackIntegrationsResponse | undefined>(key, (integrations) =>
        integrations?.filter((f) => f.integrationId === props.integrationId),
      );

      return { prevIntegrations };
    },
    onError: (error, { integrationId, receiverId, boardId }, context) => {
      showToast('Failed to update slack integration for board. Please try again.');
      reportErrorToBugsnag({
        error,
        context: 'Failed to update board slack integration',
        metadata: {
          integrationId,
          receiverId,
          boardId,
          error,
        },
      });
      if (boardId) {
        queryClient.setQueryData(getBoardSlackIntegrationsKey(boardId), context?.prevIntegrations);
      }
    },
  });

  return mutation;
};
